// src/Components/ImportMarkers/ExcelDialogDesc.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ExcelDialogDescContent from './ExcelDialogDescContent';

const normalizeLooseString = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/[\s,]+/g, '')
    .toLowerCase();
};

// Helper to cleanly extract text paragraphs from either plain text or HTML
const extractCleanParagraphs = (rawText) => {
  if (!rawText) return [];
  return rawText
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
    .replace(/<[^>]*>?/gm, '')
    .replace(/&nbsp;/g, ' ')
    .split(/\r?\n/)
    .map((p) => p.trim())
    .filter((p) => p !== '');
};

export default function ExcelDialogDesc({ state, actions }) {
  const makeUppercase = state.makeUppercase || false;

  const rteFontType = state.rteFontType || 'Helvetica';
  const rteFontSize = state.rteFontSize || '10pt';
  const imHeadingFontType = state.imHeadingFontType || 'Helvetica';
  const imHeadingFontSize = state.imHeadingFontSize || '12pt';

  const [applyToAllRows, setApplyToAllRows] = useState(false);

  const [descSearchQuery, setDescSearchQuery] = useState('');
  const [descText, setDescText] = useState('');
  const [isFetchingDesc, setIsFetchingDesc] = useState(false);
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  const [descSearchError, setDescSearchError] = useState('');

  const [overwritePrompt, setOverwritePrompt] = useState({
    open: false,
    filledCount: 0,
    totalCount: 0,
    goToNext: false,
  });

  // Expressive button state
  const [isOkPressed, setIsOkPressed] = useState(false);
  const submitRef = useRef(null);
  const cancelRef = useRef(null);
  const navPrevRef = useRef(null);
  const navNextRef = useRef(null);
  const navUpRef = useRef(null);
  const navDownRef = useRef(null);
  const enterOriginRef = useRef(null);

  const isBulkProcessingRef = useRef(false);
  const parsedDataRef = useRef(state.parsedData || []);
  const cancelLoopRef = useRef(false);

  const lastGridCellRef = useRef(null);
  const spaceTriggeredRef = useRef(false);

  // Extract necessary fields safely to avoid dependency bloat
  const isOpen = state.invalidMatchState?.open;
  const isTypeDesc = state.invalidMatchState?.type === 'DESCRIPTION';
  const rowId = state.invalidMatchState?.rowId;
  const colName = state.invalidMatchState?.colName;

  // Focus Tracker
  useEffect(() => {
    const trackFocus = (e) => {
      if (e.target && e.target.closest) {
        const cell = e.target.closest(
          'td, th, tr, [role="cell"], [role="gridcell"], [role="row"], .MuiDataGrid-cell, .MuiDataGrid-row',
        );
        if (cell) {
          lastGridCellRef.current = cell;
        }
      }
    };
    window.addEventListener('focusin', trackFocus);
    return () => window.removeEventListener('focusin', trackFocus);
  }, []);

  const restoreGridFocus = useCallback(() => {
    const activeEl = document.activeElement;
    const isTextInputReal =
      activeEl &&
      (activeEl.tagName === 'INPUT' ||
        activeEl.tagName === 'TEXTAREA' ||
        activeEl.isContentEditable ||
        (activeEl.closest && activeEl.closest('.ProseMirror')));

    // Only force blur if we are actively in an input field that shouldn't be focused anymore
    if (isTextInputReal && activeEl.tagName !== 'BODY') {
      activeEl.blur();
    }

    setTimeout(() => {
      if (lastGridCellRef.current) {
        lastGridCellRef.current.focus();
      } else {
        document.body.focus();
      }
    }, 50);
  }, []);

  // Keep a live ref to parsedData so closures don't read stale blank data
  useEffect(() => {
    parsedDataRef.current = state.parsedData || [];
  }, [state.parsedData]);

  // --- RESTORE DESCRIPTION MEMORY ON OPEN AND AT LOOP END ---
  useEffect(() => {
    if (isOpen && isTypeDesc && rowId && !isBulkProcessingRef.current) {
      setDescSearchError('');

      const row = parsedDataRef.current.find((r) => r.id === rowId);
      if (row) {
        let titleVal = '';
        const titleColKey = Object.keys(state.rowMappings || {}).find(
          (k) => state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === 'TITLE',
        );
        if (titleColKey) {
          let cName = titleColKey;
          const cRow = state.customRows?.find((r) => r.id === titleColKey);
          if (cRow && cRow.name !== '-') cName = cRow.name;
          else if (titleColKey.startsWith('prop_')) cName = titleColKey.substring(5);
          const aKey = Object.keys(row).find(
            (k) => normalizeLooseString(k) === normalizeLooseString(cName),
          );
          if (aKey && row[aKey]) titleVal = String(row[aKey]).trim();
        }
        if (!titleVal)
          titleVal = String(row['Title'] || row['title'] || row['TITLE'] || '').trim();

        const queryParts = [];
        ['TITLE', 'LOCATION ADDRESS', 'CITY', 'STATE', 'POSTCODE/ZIP', 'COUNTRY'].forEach(
          (field) => {
            const colKey = Object.keys(state.rowMappings || {}).find(
              (k) =>
                state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === field,
            );
            if (colKey) {
              let cName = colKey;
              const cRow = state.customRows?.find((r) => r.id === colKey);
              if (cRow && cRow.name !== '-') cName = cRow.name;
              else if (colKey.startsWith('prop_')) cName = colKey.substring(5);

              const aKey = Object.keys(row).find(
                (k) => normalizeLooseString(k) === normalizeLooseString(cName),
              );
              if (aKey && row[aKey]) queryParts.push(String(row[aKey]).trim());
            }
          },
        );

        if (queryParts.length === 0 && titleVal) {
          queryParts.push(titleVal);
        }

        setDescSearchQuery(queryParts.filter(Boolean).join(', '));

        // DO NOT pre-load the AI text box with the existing saved description.
        // It acts purely as a staging area for new fetches.
        setDescText('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowId, isOpen, isTypeDesc, colName]);

  const checkRowHasDescription = (row) => {
    const actualKey =
      Object.keys(row).find(
        (k) => normalizeLooseString(k) === normalizeLooseString(colName),
      ) || colName;

    const cellData = row[actualKey]
      ? String(row[actualKey])
          .replace(/<[^>]*>?/gm, '')
          .replace(/&nbsp;/g, '')
          .trim()
      : '';

    if (!cellData) return false;

    let rawTitleForQuery = '';
    const titleColKey = Object.keys(state.rowMappings || {}).find(
      (k) => state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === 'TITLE',
    );
    if (titleColKey) {
      let cName = titleColKey;
      const cRow = state.customRows?.find((r) => r.id === titleColKey);
      if (cRow && cRow.name !== '-') cName = cRow.name;
      else if (titleColKey.startsWith('prop_')) cName = titleColKey.substring(5);

      const aKey = Object.keys(row).find(
        (k) => normalizeLooseString(k) === normalizeLooseString(cName),
      );
      if (aKey && row[aKey]) rawTitleForQuery = row[aKey];
    }
    if (!rawTitleForQuery) {
      rawTitleForQuery = row['Title'] || row['title'] || row['TITLE'] || '';
    }

    const titleVal = String(rawTitleForQuery)
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

    const cleanTitleForCheck = String(titleVal)
      .replace(/<[^>]*>?/gm, '')
      .replace(/&nbsp;/g, '')
      .trim();

    let remainingData = cellData;
    if (
      cleanTitleForCheck &&
      cellData.toLowerCase().startsWith(cleanTitleForCheck.toLowerCase())
    ) {
      remainingData = cellData.substring(cleanTitleForCheck.length).trim();
    }

    return remainingData !== '';
  };

  const initiateBulkProcess = (goToNext = false) => {
    const unimportedRows =
      state.parsedData?.filter((r) => !state.successfullyImportedIds?.includes(r.id)) ||
      [];
    let filledCount = 0;

    for (let i = 0; i < unimportedRows.length; i++) {
      if (checkRowHasDescription(unimportedRows[i])) filledCount++;
    }

    if (filledCount > 0) {
      setOverwritePrompt({
        open: true,
        filledCount,
        totalCount: unimportedRows.length,
        goToNext,
      });
    } else {
      handleDescSubmit(goToNext, true);
    }
  };

  const handleFetchDescSingle = async () => {
    if (!descSearchQuery.trim()) return;

    if (applyToAllRows) {
      initiateBulkProcess(false);
      return;
    }

    setIsFetchingDesc(true);
    setDescSearchError('');
    try {
      const functions = getFunctions(getApp(), 'australia-southeast1');
      const generateDescFunction = httpsCallable(
        functions,
        'generateLocationDescription',
      );

      const result = await generateDescFunction({ query: descSearchQuery });

      if (
        result.data &&
        result.data.descriptions &&
        result.data.descriptions.length > 0
      ) {
        setDescText(result.data.descriptions.join('\n\n'));
      } else if (result.data && result.data.description) {
        setDescText(result.data.description);
      } else {
        setDescSearchError('No description could be generated for this location.');
      }
    } catch (e) {
      console.error('Generative AI Desc fetch failed:', e);
      setDescSearchError('Failed to generate description.');
    } finally {
      setIsFetchingDesc(false);
    }
  };

  const handleDescSubmit = async (goToNext = false, overwriteExisting = false) => {
    if (applyToAllRows) {
      isBulkProcessingRef.current = true;
      setIsBulkGenerating(true);
      cancelLoopRef.current = false;
      window.dispatchEvent(new CustomEvent('loktr-bulk-desc-start'));

      const unimportedRows =
        state.parsedData?.filter((r) => !state.successfullyImportedIds?.includes(r.id)) ||
        [];

      let isFirstIteration = true;
      const originalActiveRowId = rowId;

      try {
        const functions = getFunctions(getApp(), 'australia-southeast1');
        const generateDescFunction = httpsCallable(
          functions,
          'generateLocationDescription',
        );

        for (let i = 0; i < unimportedRows.length; i++) {
          if (cancelLoopRef.current) break;

          const row = unimportedRows[i];
          const isActiveRow = row.id === originalActiveRowId;

          const hasExisting = checkRowHasDescription(row);

          // 1. SKIP CONDITION: Only skip if it already has data and user declined overwrite
          if (!isActiveRow && hasExisting && !overwriteExisting) continue;

          // Highlight the row visually as it is being processed
          actions.setInvalidMatchState?.((prev) => ({ ...prev, rowId: row.id }));

          const actualKey =
            Object.keys(row).find(
              (k) => normalizeLooseString(k) === normalizeLooseString(colName),
            ) || colName;

          let rawTitleForQuery = '';
          const titleColKey = Object.keys(state.rowMappings || {}).find(
            (k) =>
              state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === 'TITLE',
          );
          if (titleColKey) {
            let cName = titleColKey;
            const cRow = state.customRows?.find((r) => r.id === titleColKey);
            if (cRow && cRow.name !== '-') cName = cRow.name;
            else if (titleColKey.startsWith('prop_')) cName = titleColKey.substring(5);

            const aKey = Object.keys(row).find(
              (k) => normalizeLooseString(k) === normalizeLooseString(cName),
            );
            if (aKey && row[aKey]) rawTitleForQuery = String(row[aKey]).trim();
          }
          if (!rawTitleForQuery) {
            rawTitleForQuery = String(
              row['Title'] || row['title'] || row['TITLE'] || '',
            ).trim();
          }

          // Force Title Case explicitly for HTML RTE Insertion
          const titleVal = String(rawTitleForQuery)
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());

          const queryParts = [];
          [
            'TITLE',
            'LOCATION ADDRESS',
            'CITY',
            'STATE',
            'POSTCODE/ZIP',
            'COUNTRY',
          ].forEach((field) => {
            const colKey = Object.keys(state.rowMappings || {}).find(
              (k) =>
                state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === field,
            );
            if (colKey) {
              let cName = colKey;
              const cRow = state.customRows?.find((r) => r.id === colKey);
              if (cRow && cRow.name !== '-') cName = cRow.name;
              else if (colKey.startsWith('prop_')) cName = colKey.substring(5);

              const aKey = Object.keys(row).find(
                (k) => normalizeLooseString(k) === normalizeLooseString(cName),
              );
              if (aKey && row[aKey]) queryParts.push(String(row[aKey]).trim());
            }
          });

          if (queryParts.length === 0 && rawTitleForQuery) {
            queryParts.push(rawTitleForQuery);
          }
          const finalQueryParts = queryParts.filter(Boolean).join(', ');

          if (!finalQueryParts) {
            await new Promise((res) => setTimeout(res, 50));
            continue;
          }

          let finalDesc = '';
          let activeQuery = finalQueryParts;

          if (isActiveRow && descSearchQuery.trim() !== '') {
            activeQuery = descSearchQuery;
          }

          // 2. FETCH OR ASSIGN TEXT LOGIC
          if (
            isActiveRow &&
            descText.trim() !== '' &&
            descText.trim() !== 'Generating...'
          ) {
            finalDesc = descText;
            setDescSearchQuery(activeQuery);
            isFirstIteration = false;
          } else {
            if (isFirstIteration) {
              setDescSearchQuery(activeQuery);
              setDescText('');
              isFirstIteration = false;
            } else {
              setDescSearchQuery(activeQuery);
              setDescText('Generating...');
            }

            // Yield to the main thread for 50ms so React can paint the active row background before blocking
            await new Promise((res) => setTimeout(res, 50));

            try {
              const result = await generateDescFunction({ query: activeQuery });

              if (
                result.data &&
                result.data.descriptions &&
                result.data.descriptions.length > 0
              ) {
                finalDesc = result.data.descriptions[0];
              } else if (result.data && result.data.description) {
                finalDesc = result.data.description;
              }
              setDescText(finalDesc || '');
            } catch (e) {
              console.error('Auto AI desc scrape failed for', activeQuery, e);
              setDescText('Failed to generate.');
              await new Promise((res) => setTimeout(res, 500));
              continue;
            }
          }

          if (!finalDesc) continue;

          // 3. APPLY FORMATTING RULES TO THE TEXT
          let completedRow = null;

          actions.setParsedData?.((prev) =>
            prev.map((r) => {
              if (r.id === row.id) {
                const newMatchedFiles = r.matchedFiles ? [...r.matchedFiles] : [];
                const paragraphs = extractCleanParagraphs(finalDesc);

                const titleHtml = `<p><span style="font-family: ${imHeadingFontType}; font-size: ${imHeadingFontSize};"><strong>${titleVal}</strong></span></p>`;

                let updatedDescValue = finalDesc;

                if (newMatchedFiles.length > 0 && paragraphs.length > 0) {
                  newMatchedFiles.forEach((file, index) => {
                    const pIndexStart = index === 0 ? 0 : index + 1;
                    const pIndexEnd = index === 0 ? 2 : index + 2;

                    const fileDescParas = paragraphs.slice(pIndexStart, pIndexEnd);
                    let fileDescText = fileDescParas
                      .map(
                        (p) =>
                          `<p><span style="font-family: ${rteFontType}; font-size: ${rteFontSize};">${p}</span></p>`,
                      )
                      .join('');

                    if (index === 0) {
                      fileDescText = titleHtml + fileDescText;
                    }

                    file.description = fileDescText || '';
                  });
                  updatedDescValue = finalDesc;
                } else if (paragraphs.length > 0) {
                  const fileDescParas = paragraphs.slice(0, 2);
                  let fileDescText = fileDescParas
                    .map(
                      (p) =>
                        `<p><span style="font-family: ${rteFontType}; font-size: ${rteFontSize};">${p}</span></p>`,
                    )
                    .join('');
                  updatedDescValue = titleHtml + fileDescText;
                } else {
                  updatedDescValue = '';
                }

                completedRow = {
                  ...r,
                  [actualKey]: updatedDescValue || r[actualKey],
                  matchedFiles: newMatchedFiles,
                  _manuallyAssigned: {
                    ...(r._manuallyAssigned || {}),
                    [actualKey]: true,
                  },
                };
                return completedRow;
              }
              return r;
            }),
          );

          // Broadcast execution state change instantly to left panel view
          if (completedRow) {
            window.dispatchEvent(
              new CustomEvent('loktr-bulk-desc-update', { detail: completedRow }),
            );
          }

          // Yield briefly for paint cycle before loop advances
          await new Promise((res) => setTimeout(res, 50));
        }
      } catch (error) {
        console.error('Error during bulk description assignment:', error);
      } finally {
        // Retain the intentional 1 second pause explicitly on completion context
        await new Promise((res) => setTimeout(res, 1000));

        isBulkProcessingRef.current = false;
        setIsBulkGenerating(false);
        window.dispatchEvent(new CustomEvent('loktr-bulk-desc-end'));

        let nextRowIdToTrigger = null;

        // Revert cursor to the very first row safely
        const allRows = parsedDataRef.current || [];
        if (allRows.length > 0) {
          const firstRow = allRows[0];
          nextRowIdToTrigger = firstRow.id;

          let titleVal = '';
          const titleColKey = Object.keys(state.rowMappings || {}).find(
            (k) =>
              state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === 'TITLE',
          );
          if (titleColKey) {
            let colName = titleColKey;
            const cRow = state.customRows?.find((r) => r.id === titleColKey);
            if (cRow && cRow.name !== '-') colName = cRow.name;
            else if (titleColKey.startsWith('prop_')) colName = titleColKey.substring(5);
            const actualKey = Object.keys(firstRow).find(
              (k) => normalizeLooseString(k) === normalizeLooseString(colName),
            );
            if (actualKey) titleVal = String(firstRow[actualKey]).trim();
          }
          if (!titleVal)
            titleVal = String(
              firstRow['Title'] || firstRow['title'] || firstRow['TITLE'] || '',
            ).trim();

          const queryParts = [];
          [
            'TITLE',
            'LOCATION ADDRESS',
            'CITY',
            'STATE',
            'POSTCODE/ZIP',
            'COUNTRY',
          ].forEach((field) => {
            const colKey = Object.keys(state.rowMappings || {}).find(
              (k) =>
                state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === field,
            );
            if (colKey) {
              let cName = colKey;
              const cRow = state.customRows?.find((r) => r.id === colKey);
              if (cRow && cRow.name !== '-') cName = cRow.name;
              else if (colKey.startsWith('prop_')) cName = colKey.substring(5);

              const aKey = Object.keys(firstRow).find(
                (k) => normalizeLooseString(k) === normalizeLooseString(cName),
              );
              if (aKey && firstRow[aKey]) queryParts.push(String(firstRow[aKey]).trim());
            }
          });

          if (queryParts.length === 0 && titleVal) {
            queryParts.push(titleVal);
          }
          setDescSearchQuery(queryParts.filter(Boolean).join(', '));

          actions.setInvalidMatchState?.((prev) => ({ ...prev, rowId: firstRow.id }));
        } else {
          setDescSearchQuery('');
        }

        // Always clear the input text box when done processing
        setDescText('');
        setTimeout(() => setApplyToAllRows(false), 200);

        restoreGridFocus();

        if (goToNext && actions.setPendingNextColumn && nextRowIdToTrigger) {
          actions.setPendingNextColumn({ rowId: nextRowIdToTrigger, colName });
        }
      }
    } else {
      // --- SINGLE ROW LOGIC ---

      if (!descText.trim() || descText.trim() === 'Generating...') {
        setDescText('');
        setDescSearchQuery('');
        if (goToNext && actions.setPendingNextColumn) {
          actions.setPendingNextColumn({ rowId, colName });
        }
        return;
      }

      actions.setParsedData?.((prev) =>
        prev.map((row) => {
          if (row.id === rowId) {
            const actualRowKey =
              Object.keys(row).find(
                (k) => normalizeLooseString(k) === normalizeLooseString(colName),
              ) || colName;

            let rawTitleForQuery = '';
            const titleColKey = Object.keys(state.rowMappings || {}).find(
              (k) =>
                state.rowMappings[k] === 'INFO' && state.rowSubMappings?.[k] === 'TITLE',
            );
            if (titleColKey) {
              let cName = titleColKey;
              const cRow = state.customRows?.find((r) => r.id === titleColKey);
              if (cRow && cRow.name !== '-') cName = cRow.name;
              else if (titleColKey.startsWith('prop_')) cName = titleColKey.substring(5);

              const aKey = Object.keys(row).find(
                (k) => normalizeLooseString(k) === normalizeLooseString(cName),
              );
              if (aKey && row[aKey]) rawTitleForQuery = String(row[aKey]).trim();
            }
            if (!rawTitleForQuery) {
              rawTitleForQuery = String(
                row['Title'] || row['title'] || row['TITLE'] || '',
              ).trim();
            }

            // Force Title Case explicitly
            const titleVal = String(rawTitleForQuery)
              .toLowerCase()
              .replace(/\b\w/g, (char) => char.toUpperCase());

            const newMatchedFiles = row.matchedFiles ? [...row.matchedFiles] : [];
            const paragraphs = extractCleanParagraphs(descText);

            const titleHtml = `<p><span style="font-family: ${imHeadingFontType}; font-size: ${imHeadingFontSize};"><strong>${titleVal}</strong></span></p>`;

            let updatedDescValue = descText;

            if (newMatchedFiles.length > 0 && paragraphs.length > 0) {
              newMatchedFiles.forEach((file, index) => {
                const pIndexStart = index === 0 ? 0 : index + 1;
                const pIndexEnd = index === 0 ? 2 : index + 2;

                const fileDescParas = paragraphs.slice(pIndexStart, pIndexEnd);
                let fileDescText = fileDescParas
                  .map(
                    (p) =>
                      `<p><span style="font-family: ${rteFontType}; font-size: ${rteFontSize};">${p}</span></p>`,
                  )
                  .join('');

                if (index === 0) {
                  fileDescText = titleHtml + fileDescText;
                }

                file.description = fileDescText || '';
              });
              updatedDescValue = descText;
            } else if (paragraphs.length > 0) {
              // No photo rule: exact truncate to first two paragraphs only
              const fileDescParas = paragraphs.slice(0, 2);
              let fileDescText = fileDescParas
                .map(
                  (p) =>
                    `<p><span style="font-family: ${rteFontType}; font-size: ${rteFontSize};">${p}</span></p>`,
                )
                .join('');
              updatedDescValue = titleHtml + fileDescText;
            } else {
              updatedDescValue = '';
            }

            return {
              ...row,
              [actualRowKey]: updatedDescValue,
              matchedFiles: newMatchedFiles,
              _manuallyAssigned: {
                ...(row._manuallyAssigned || {}),
                [actualRowKey]: true,
              },
            };
          }
          return row;
        }),
      );

      // Clear the text box so that hitting OK again does not overwrite the RTE
      setDescText('');
      setDescSearchQuery('');

      restoreGridFocus();

      if (goToNext && actions.setPendingNextColumn) {
        actions.setPendingNextColumn({ rowId, colName });
      }
    }
  };

  const handleCancelClick = () => {
    if (isBulkGenerating && applyToAllRows) {
      cancelLoopRef.current = true;
      setDescText('Cancelling...');
    } else if (descText.trim() !== '' || descSearchError !== '') {
      setDescText('');
      setDescSearchError('');
      restoreGridFocus();
    } else {
      handleDescCancel();
    }
  };

  const handleDescCancel = () => {
    setDescText('');
    setDescSearchQuery('');
    actions.setInvalidMatchState?.({
      open: false,
      rowId: null,
      colName: null,
      cellValue: '',
      type: null,
    });

    restoreGridFocus();
  };

  const handleCopyText = async () => {
    if (!descText) return;
    try {
      await navigator.clipboard.writeText(descText);
    } catch (err) {
      console.error('Failed to copy text', err);
    }
  };

  const handleNavPrev = () => {
    if (actions.setPendingNextColumn) {
      actions.setPendingNextColumn({
        rowId: state.invalidMatchState?.rowId,
        colName: state.invalidMatchState?.colName,
        direction: 'prev',
      });
    }
  };

  const handleNavNext = () => {
    if (actions.setPendingNextColumn) {
      actions.setPendingNextColumn({
        rowId: state.invalidMatchState?.rowId,
        colName: state.invalidMatchState?.colName,
        direction: 'next',
      });
    }
  };

  const handleNavUp = () => {
    if (actions.setPendingNextColumn) {
      actions.setPendingNextColumn({
        rowId: state.invalidMatchState?.rowId,
        colName: state.invalidMatchState?.colName,
        rowDirection: 'up',
      });
    }
  };

  const handleNavDown = () => {
    if (actions.setPendingNextColumn) {
      actions.setPendingNextColumn({
        rowId: state.invalidMatchState?.rowId,
        colName: state.invalidMatchState?.colName,
        rowDirection: 'down',
      });
    }
  };

  const isDescOkDisabled = isFetchingDesc || (isBulkGenerating && !applyToAllRows);

  // Bind refs for keyboard listeners
  useEffect(() => {
    submitRef.current = () => {
      if (!isDescOkDisabled) {
        if (applyToAllRows) initiateBulkProcess(false);
        else handleDescSubmit(false, false);
      }
    };
    cancelRef.current = handleCancelClick;
    navPrevRef.current = handleNavPrev;
    navNextRef.current = handleNavNext;
    navUpRef.current = handleNavUp;
    navDownRef.current = handleNavDown;
  });

  // Universally Permissive Global Key Listener
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (!isOpen || !isTypeDesc) return;

      const activeEl = document.activeElement;

      const inSidePanel = !!(activeEl && activeEl.closest('#excel-desc-panel'));

      const isTextInputReal =
        activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.isContentEditable ||
          (activeEl.closest && activeEl.closest('.ProseMirror')));

      const isControl =
        inSidePanel &&
        (activeEl.tagName === 'BUTTON' ||
          activeEl.type === 'radio' ||
          activeEl.type === 'checkbox' ||
          activeEl.getAttribute('role') === 'combobox' ||
          activeEl.classList?.contains('MuiSelect-select') ||
          activeEl.getAttribute('role') === 'menuitem' ||
          activeEl.getAttribute('role') === 'option' ||
          activeEl.getAttribute('role') === 'switch');

      const isListBox = document.querySelector('[role="listbox"]');

      if (e.key === 'Escape') {
        if (isListBox) return;

        if (inSidePanel) {
          e.preventDefault();
          e.stopPropagation();
          restoreGridFocus();
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        if (cancelRef.current) cancelRef.current();
        return;
      }

      if (isListBox) {
        if (e.key === 'Enter') enterOriginRef.current = 'listbox';
        return;
      }

      // --- TRIGGER SEARCH VIA SPACE ---
      if (!isTextInputReal && !isControl && (e.key === ' ' || e.code === 'Space')) {
        e.preventDefault();
        e.stopPropagation();
        spaceTriggeredRef.current = true;

        const btn = document.getElementById('desc-search-button');
        if (btn && !btn.disabled) {
          btn.click();
        } else {
          let attempts = 0;
          const focusInterval = setInterval(() => {
            const input = document.getElementById('desc-search-input');
            if (input && !input.disabled) {
              input.focus();
              try {
                const len = input.value.length;
                input.setSelectionRange(len, len);
              } catch (err) {}
              clearInterval(focusInterval);
            }
            attempts++;
            if (attempts > 15) clearInterval(focusInterval);
          }, 50);
        }
        return;
      }

      if (isTextInputReal) {
        if (e.key !== 'Enter') return;
        // Let Shift+Enter insert newlines normally in textareas
        if (
          (activeEl.tagName === 'TEXTAREA' || activeEl.isContentEditable) &&
          e.shiftKey
        ) {
          return;
        }
      }

      if (e.key === 'Enter') {
        const isComboBox =
          activeEl &&
          (activeEl.getAttribute('role') === 'combobox' ||
            activeEl.classList?.contains('MuiSelect-select'));
        if (isComboBox) return;
        enterOriginRef.current = 'submit';
        e.preventDefault();
        e.stopPropagation();
        setIsOkPressed(true);
        return;
      }

      // --- GRID NAVIGATION ---
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();

        if (activeEl && activeEl.tagName !== 'BODY') {
          activeEl.blur();
        }

        if (e.key === 'ArrowLeft' && navPrevRef.current) navPrevRef.current();
        if (e.key === 'ArrowRight' && navNextRef.current) navNextRef.current();
        if (e.key === 'ArrowUp' && navUpRef.current) navUpRef.current();
        if (e.key === 'ArrowDown' && navDownRef.current) navDownRef.current();
      }
    };

    const handleGlobalKeyUp = (e) => {
      if (!isOpen || !isTypeDesc) return;

      if (e.key === 'Escape') return;

      if (e.key === ' ' || e.code === 'Space') {
        if (spaceTriggeredRef.current) {
          e.preventDefault();
          e.stopPropagation();
          spaceTriggeredRef.current = false;
          return;
        }
      }

      const activeEl = document.activeElement;

      if (activeEl && (activeEl.tagName === 'TEXTAREA' || activeEl.isContentEditable)) {
        if (e.key !== 'Enter' || e.shiftKey) return;
      } else if (activeEl && activeEl.tagName === 'INPUT' && e.key !== 'Enter') {
        return;
      }

      if (e.key === 'Enter') {
        const origin = enterOriginRef.current;
        enterOriginRef.current = null;

        if (origin === 'listbox') {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => {
            if (submitRef.current) submitRef.current();
          }, 150);
          return;
        }

        if (origin !== 'submit') return;

        e.preventDefault();
        e.stopPropagation();
        setIsOkPressed(false);
        if (submitRef.current) submitRef.current();
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown, { capture: true });
    window.addEventListener('keyup', handleGlobalKeyUp, { capture: true });
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown, { capture: true });
      window.removeEventListener('keyup', handleGlobalKeyUp, { capture: true });
    };
  }, [isOpen, isTypeDesc, restoreGridFocus]);

  return (
    <ExcelDialogDescContent
      isOpen={isOpen}
      isTypeDesc={isTypeDesc}
      makeUppercase={makeUppercase}
      colName={colName}
      descSearchQuery={descSearchQuery}
      setDescSearchQuery={setDescSearchQuery}
      handleFetchDescSingle={handleFetchDescSingle}
      isFetchingDesc={isFetchingDesc}
      isBulkGenerating={isBulkGenerating}
      descSearchError={descSearchError}
      descText={descText}
      setDescText={setDescText}
      handleCopyText={handleCopyText}
      overwritePrompt={overwritePrompt}
      setOverwritePrompt={setOverwritePrompt}
      handleDescSubmit={handleDescSubmit}
      applyToAllRows={applyToAllRows}
      setApplyToAllRows={setApplyToAllRows}
      handleCancelClick={handleCancelClick}
      handleNavUp={handleNavUp}
      handleNavDown={handleNavDown}
      handleNavPrev={handleNavPrev}
      handleNavNext={handleNavNext}
      isDescOkDisabled={isDescOkDisabled}
      initiateBulkProcess={initiateBulkProcess}
      isOkPressed={isOkPressed}
    />
  );
}
